import { AfterViewInit, Component, ContentChildren, ElementRef, EventEmitter, input, Input, OnDestroy, Output, QueryList, ViewChild } from '@angular/core';
import { register } from 'swiper/element/bundle';

import { CarouselItemDirective } from '../directives/carousel-item.directive';

@Component({
    selector: 'lib-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    host: {
        '[class]': "[this.showNavButtons ? '' : 'hide-buttons']"
    },
    standalone: false
})
export class CarouselComponent implements AfterViewInit, OnDestroy {

  @ContentChildren(CarouselItemDirective) carouselItems: QueryList<CarouselItemDirective>;

  @ViewChild('swiperContainer') swiperContainer: ElementRef;
  @ViewChild('navButtonPrev') navButtonPrev: ElementRef;
  @ViewChild('navButtonNext') navButtonNext: ElementRef;

  @Input() maxSlidesPerView: number | "auto" = "auto";
  @Input() selectedItem: number = 0;
  @Input() showNavButtons: boolean = true;
  @Input() customArrows: boolean = false;
  @Input() showPagination: boolean = false;
  @Input() showScrollbar: boolean = false;
  @Input() mobileSlidesPerView: number = 1;
  mobileHeight = input<number | null>(null);
  spaceBetween = input<number>(36);
  autoHeight = input<boolean>(true);

  @Input() set currentSlide(slideNumber: number) {
    const swiperEl = this.swiperContainer?.nativeElement;

    if (!!swiperEl) {
      swiperEl.swiper.slideTo(slideNumber, 300);
    }
  }

  @Output() slideChange = new EventEmitter<number>();

  #swiperObserver: MutationObserver | null = null;

  swiperConfig: any;

  constructor() {
    register();
  }

  ngOnDestroy(): void {
    if (this.#swiperObserver) {
      this.#swiperObserver.disconnect();
    }
  }

  ngAfterViewInit(): void {
    const swiperEl = this.swiperContainer?.nativeElement;

    if (!!swiperEl) {
      this.#watchHeight();

      swiperEl.addEventListener('activeindexchange', (event: Event) => {
        const [ swiper ] = event['detail'];

        this.#onIndexChange(swiper.activeIndex);
      });

      this.swiperConfig = {
        injectStyles: [
          `
            .swiper {
              overflow: visible;
            }

            .swiper-backface-hidden ::slotted(swiper-slide) {
              transform: unset;
              position: unset;
            }

            @media(max-width: 768px){
              ::slotted(swiper-slide) {
                height: ${this.mobileHeight() ? this.mobileHeight() +'px' : 'auto'} !important;
              }            
            }

          `
        ],
        direction: 'horizontal',
        slidesPerView: this.mobileSlidesPerView,
        autoHeight: this.autoHeight(),
        pagination: this.showPagination ? {
          clickable: true,
          hideOnClick: false,
        } : false,
        scrollbar: this.showScrollbar ? {
          draggable: true,
          snapOnRelease: true
        } : false,
        navigation: this.showNavButtons ? {
          nextEl: this.navButtonNext?.nativeElement,
          prevEl: this.navButtonPrev?.nativeElement,
        } : false,
        spaceBetween: this.spaceBetween(),
        initialSlide: this.selectedItem,
        centeredSlides: true,
        watchOverflow: true,
        watchSlidesVisibility: true,
        observer: true,
        centerInsufficientSlides: false,
        breakpoints: {
          768: {
            slidesPerView: this.maxSlidesPerView,
            centeredSlides: false,
            centerInsufficientSlides: true,
            shortSwipes: false
          },
        }
      };

      Object.assign(swiperEl, this.swiperConfig);

      swiperEl.initialize();
    }
  }

  #watchHeight(): void {
    const swiperEl = this.swiperContainer?.nativeElement;
    const config: MutationObserverInit = {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    };

    if (!!swiperEl) {
      this.#swiperObserver = new MutationObserver(() => {
        swiperEl.swiper.updateAutoHeight();
      });

      this.#swiperObserver.observe(swiperEl, config);
    }
  }

  #onIndexChange(index: number): void {
    this.slideChange.emit(index);
  }

}
